import crypto from 'crypto';

import { authLogger } from '@/lib/auth/logger';
import type {
  Address,
  AddressType,
  Capacity,
  Complaint,
  ComplaintStatusFilter,
  Contact,
  DeliveryNotice,
  Employee,
  FileUploadResponse,
  Order,
  PmAuth,
  ServiceApiResponse,
  User,
} from '@/types/service-api';

export class ServiceApiClient {
  private baseUrl: string;
  private salt: string;

  constructor(baseUrl?: string, salt?: string) {
    this.baseUrl = baseUrl || process.env.NEXT_PUBLIC_SERVICE_API_URL || '';
    this.salt = salt || process.env.SERVICE_API_SALT || '';

    // Only validate if running on server (Node.js environment)
    // Client-side code should use API routes, not this client directly
    if (typeof window === 'undefined') {
      if (!this.baseUrl) {
        throw new Error('Service API URL is not configured');
      }

      if (!this.salt) {
        throw new Error('Service API salt is not configured');
      }
    }
  }

  /**
   * Generate Pm-Auth header value
   * Hash format: SHA512 of "[mail]|[time]|[salt]"
   */
  private generateAuthHeader(mail: string): { header: string; authData: PmAuth } {
    const time = Math.floor(Date.now() / 1000).toString();
    const hashString = `${mail}|${time}|${this.salt}`;
    const hash = crypto.createHash('sha512').update(hashString).digest('hex');

    authLogger.log('🔐 [CLIENT] Generating Pm-Auth header:');
    authLogger.log('🔐 [CLIENT]   Mail:', mail);
    authLogger.log('🔐 [CLIENT]   Time:', time);
    authLogger.log('🔐 [CLIENT]   Salt set:', !!this.salt);
    authLogger.log('🔐 [CLIENT]   Salt length:', this.salt.length);
    authLogger.log('🔐 [CLIENT]   Hash string:', hashString);
    authLogger.log('🔐 [CLIENT]   Hash:', hash);

    const authData: PmAuth = {
      mail,
      time,
      hash,
    };

    const header = Buffer.from(JSON.stringify(authData)).toString('base64');
    authLogger.log('🔐 [CLIENT]   Auth Data:', JSON.stringify(authData, null, 2));
    authLogger.log('🔐 [CLIENT]   Base64 Header:', header);

    return { header, authData };
  }

  /**
   * Make authenticated request to Service API
   */
  private async request<T>(
    endpoint: string,
    mail: string,
    options: RequestInit = {}
  ): Promise<ServiceApiResponse<T>> {
    try {
      const { header } = this.generateAuthHeader(mail);

      const url = `${this.baseUrl}${endpoint}`;
      authLogger.log('🌐 [CLIENT] Making request to:', url);
      authLogger.log('🌐 [CLIENT] Method:', options.method || 'GET');

      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Pm-Auth': header,
          ...options.headers,
        },
      });

      authLogger.log('🌐 [CLIENT] Response status:', response.status);
      authLogger.log('🌐 [CLIENT] Response ok:', response.ok);

      const data = await response.json();
      authLogger.log('🌐 [CLIENT] Response data:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        authLogger.error('❌ [CLIENT] Request failed with status:', response.status);
        authLogger.error('❌ [CLIENT] Error data:', data);
        return {
          error: data,
          status: response.status,
        };
      }

      return {
        data,
        status: response.status,
      };
    } catch (error) {
      authLogger.error('❌ [CLIENT] Request exception:', error);
      return {
        error: {
          message:
            error instanceof Error ? error.message : 'An unknown error occurred',
        },
        status: 500,
      };
    }
  }

  /**
   * Get address for customerNr
   */
  async getAddressByCustomerNr(
    customerNr: number,
    mail: string
  ): Promise<ServiceApiResponse<Address>> {
    return this.request<Address>(`/address/${customerNr}`, mail);
  }

  /**
   * Get address of type for specific order
   */
  async getAddressByOrder(
    orderNr: number,
    orderSubNr: number,
    addressType: AddressType,
    mail: string
  ): Promise<ServiceApiResponse<Address>> {
    return this.request<Address>(
      `/address/${orderNr}/${orderSubNr}?addressType=${addressType}`,
      mail
    );
  }

  /**
   * Get data for User
   */
  async getUser(mail: string): Promise<ServiceApiResponse<User>> {
    return this.request<User>('/user', mail);
  }

  /**
   * Get production capacity overview for next 6 weeks
   */
  async getCapacity(mail: string): Promise<ServiceApiResponse<Capacity>> {
    return this.request<Capacity>('/capacity', mail);
  }

  /**
   * Get list of all contacts from customer
   */
  async getContacts(
    customerNr: string,
    mail: string
  ): Promise<ServiceApiResponse<Contact[]>> {
    return this.request<Contact[]>(`/contact/list?customerNr=${customerNr}`, mail);
  }

  /**
   * Get complaint overview in PDF form (returns raw PDF bytes)
   */
  async getComplaintOverview(
    complaintId: number,
    mail: string
  ): Promise<
    { buffer: Buffer; status: number } | { error: unknown; status: number }
  > {
    try {
      const { header } = this.generateAuthHeader(mail);

      const url = `${this.baseUrl}/complaint/${complaintId}/overview`;
      authLogger.log('🌐 [CLIENT] Making request to:', url);

      const response = await fetch(url, {
        headers: {
          'Pm-Auth': header,
        },
      });

      authLogger.log('🌐 [CLIENT] Response status:', response.status);

      if (!response.ok) {
        return {
          error: { message: `Request failed with status ${response.status}` },
          status: response.status,
        };
      }

      const buffer = Buffer.from(await response.arrayBuffer());
      return { buffer, status: 200 };
    } catch (error) {
      authLogger.error('❌ [CLIENT] Request exception:', error);
      return {
        error: {
          message:
            error instanceof Error ? error.message : 'An unknown error occurred',
        },
        status: 500,
      };
    }
  }

  /**
   * Get list of all complaints from customer
   */
  async getComplaints(
    customerNr: string,
    complaintStatus: ComplaintStatusFilter = 'NotCompleted',
    mail: string
  ): Promise<ServiceApiResponse<Complaint[]>> {
    return this.request<Complaint[]>(
      `/complaint/list?customerNr=${customerNr}&complaintStatus=${complaintStatus}`,
      mail
    );
  }

  /**
   * Create a new complaint
   */
  async createComplaint(
    complaint: Complaint,
    mail: string
  ): Promise<ServiceApiResponse<Complaint>> {
    return this.request<Complaint>('/complaint', mail, {
      method: 'POST',
      body: JSON.stringify(complaint),
    });
  }

  /**
   * Find order by order number and order subnumber
   */
  async getOrder(
    orderNr: number,
    orderSubNr: number,
    mail: string
  ): Promise<ServiceApiResponse<Order>> {
    return this.request<Order>(`/order/${orderNr}/${orderSubNr}`, mail);
  }

  /**
   * Get list of delivery notices for customer
   */
  async getDeliveryNotices(
    customerNr: number,
    mail: string
  ): Promise<ServiceApiResponse<DeliveryNotice[]>> {
    return this.request<DeliveryNotice[]>(`/deliveryNotice/${customerNr}`, mail);
  }

  /**
   * Get presence status for employee
   */
  async getEmployeePresence(
    employeeShort: string,
    mail: string
  ): Promise<ServiceApiResponse<Employee>> {
    return this.request<Employee>(`/employee/${employeeShort}/presence`, mail);
  }

  /**
   * Upload file to a complaint
   * Uses multipart/form-data instead of JSON
   */
  async uploadComplaintFile(
    complaintId: number,
    file: File,
    customerNr: string,
    mail: string
  ): Promise<ServiceApiResponse<FileUploadResponse>> {
    try {
      const { header } = this.generateAuthHeader(mail);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('customerNr', customerNr);

      const url = `${this.baseUrl}/file/upload/${complaintId}`;
      authLogger.log('🌐 [CLIENT] Uploading file to:', url);
      authLogger.log('🌐 [CLIENT] File name:', file.name);
      authLogger.log('🌐 [CLIENT] File size:', file.size);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          // IMPORTANT: Do NOT set Content-Type header for FormData!
          // The browser automatically sets the correct multipart/form-data header with boundary
          'Pm-Auth': header,
        },
        body: formData,
      });

      authLogger.log('🌐 [CLIENT] Response status:', response.status);

      const data = await response.json();

      if (!response.ok) {
        return {
          error: data,
          status: response.status,
        };
      }

      return {
        data,
        status: response.status,
      };
    } catch (error) {
      authLogger.error('❌ [CLIENT] Upload exception:', error);
      return {
        error: {
          message:
            error instanceof Error ? error.message : 'An unknown error occurred',
        },
        status: 500,
      };
    }
  }
}

// Singleton instance
export const serviceApiClient = new ServiceApiClient();
